import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import './CreateTestRunPage.css';

interface Folder {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  level: number;
}

const urlParams = new URLSearchParams(window.location.search);
const URL_FOLDER_ID = urlParams.get('folder_id') || '';
const URL_RUN_TYPE: 'SCENARIO' | 'TEST_CASE' =
  urlParams.get('type') === 'test_case' ? 'TEST_CASE' : 'SCENARIO';

const ENVIRONMENTS = [
  { value: 'Stage', label: 'Stage' },
  { value: 'Preprod', label: 'Preprod' },
  { value: 'PROD', label: 'Production' },
];

const CreateTestRunPage: React.FC = () => {
  const navigate = useNavigate();
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const manualRunTypeChange = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    folder_id: URL_FOLDER_ID,
    run_type: URL_RUN_TYPE as string,
    build_version: '',
    environment: '',
    cycle_label: ''
  });
  const [existingCycleLabels, setExistingCycleLabels] = useState<string[]>([]);

  useEffect(() => {
    fetchFolders(URL_RUN_TYPE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!manualRunTypeChange.current) return;
    manualRunTypeChange.current = false;
    fetchFolders(formData.run_type as 'SCENARIO' | 'TEST_CASE', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.run_type]);

  const fetchFolders = async (runType: 'SCENARIO' | 'TEST_CASE', clearSelections = false) => {
    try {
      const folderType = runType === 'SCENARIO' ? 'test_scenario' : 'test_case';
      const folderList = await api.getFolders(folderType);
      setAllFolders(folderList);
      if (clearSelections) {
        setFormData(prev => ({ ...prev, project_id: '', folder_id: '' }));
      } else if (URL_FOLDER_ID) {
        const projectId = findRootProject(folderList, URL_FOLDER_ID);
        setFormData(prev => ({ ...prev, project_id: projectId }));
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load folders');
      setLoading(false);
    }
  };

  const findRootProject = (folders: Folder[], folderId: string): string => {
    let current: Folder | undefined = folders.find(f => f.id === folderId);
    while (current) {
      if (current.level === 0) return current.id;
      const parentId = current.parent_id;
      current = parentId ? folders.find(f => f.id === parentId) : undefined;
    }
    return '';
  };

  const projectFolders = allFolders.filter(f => f.level === 0);

  const subfolders = formData.project_id
    ? allFolders.filter(f => {
        if (f.level === 0) return false;
        if ((f as any).review_status !== 'Approved') return false;
        let current: Folder | undefined = f;
        while (current && current.parent_id) {
          const pid: string = current.parent_id;
          if (pid === formData.project_id) return true;
          current = allFolders.find(folder => folder.id === pid);
        }
        return false;
      })
    : [];

  // Check if selected subfolder (or project if no subfolder) is Approved
  const selectedFolder = formData.folder_id
    ? allFolders.find(f => f.id === formData.folder_id)
    : formData.project_id
      ? allFolders.find(f => f.id === formData.project_id)
      : null;
  const selectedFolderStatus = (selectedFolder as any)?.review_status;
  const isApproved = selectedFolderStatus === 'Approved';
  const notApprovedReason = selectedFolder
    ? `This folder is "${selectedFolderStatus || 'Draft'}" — only Approved folders can be used for Test Runs`
    : '';

  const getFolderPath = (folder: Folder): string => {
    const path: string[] = [folder.name];
    let current: Folder | undefined = folder;
    while (current && current.parent_id) {
      const pid: string = current.parent_id;
      const parent: Folder | undefined = allFolders.find(f => f.id === pid);
      if (parent && parent.level > 0) path.unshift(parent.name);
      current = parent;
    }
    return path.join(' / ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'run_type') {
      manualRunTypeChange.current = true;
      setFormData(prev => ({ ...prev, run_type: value }));
    } else if (name === 'project_id') {
      setFormData(prev => ({ ...prev, project_id: value, folder_id: '', cycle_label: '' }));
      setExistingCycleLabels([]);
    } else if (name === 'folder_id') {
      setFormData(prev => ({ ...prev, folder_id: value }));
      // Auto-fetch cycle labels for this subfolder
      if (value) {
        api.getCycleLabelsForFolder(value).then(labels => {
          setExistingCycleLabels(labels);
          if (labels.length > 0) {
            // Auto-populate with the most recent (last alphabetically) label
            setFormData(prev => ({ ...prev, folder_id: value, cycle_label: labels[labels.length - 1] }));
          } else {
            setFormData(prev => ({ ...prev, folder_id: value }));
          }
        }).catch(() => {
          setExistingCycleLabels([]);
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) { setError('Please select a project'); return; }
    if (!formData.folder_id) { setError('Please select a subfolder'); return; }
    if (!formData.environment) { setError('Please select an environment'); return; }
    if (!formData.cycle_label.trim()) { setError('Please enter a Cycle Label'); return; }
    try {
      setSaving(true);
      setError(null);
      const testRun = await api.createTestRun({
        name: formData.name,
        description: formData.description || undefined,
        folder_id: formData.folder_id,
        run_type: formData.run_type as 'SCENARIO' | 'TEST_CASE',
        build_version: formData.build_version || undefined,
        environment: formData.environment || undefined,
        cycle_label: formData.cycle_label.trim() || undefined
      });
      navigate(`/test-runs/${testRun.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create test run');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="ctr-page"><div className="ctr-loading">Loading...</div></div>;
  }

  const selectedProject = projectFolders.find(f => f.id === formData.project_id);
  const selectedSubfolder = subfolders.find(f => f.id === formData.folder_id);

  return (
    <div className="ctr-page">
      {/* Back link */}
      <button className="ctr-back" onClick={() => navigate('/test-runs')}>
        ← Back to Test Runs
      </button>

      {/* Page title */}
      <h1 className="ctr-title">Create Test Run</h1>
      <p className="ctr-subtitle">Configure and initiate a new execution sequence across your infrastructure.</p>

      {error && (
        <div className="ctr-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Form card */}
      <form className="ctr-card" onSubmit={handleSubmit}>

        {/* Test Run Name */}
        <div className="ctr-field">
          <label className="ctr-label">TEST RUN NAME <span className="ctr-required">*</span></label>
          <input
            className="ctr-input"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Q4_Core_Checkout_Regression_v1.2"
            required
          />
        </div>

        {/* Description */}
        <div className="ctr-field">
          <label className="ctr-label">DESCRIPTION</label>
          <textarea
            className="ctr-input ctr-textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe the scope of this execution sequence..."
            rows={4}
          />
        </div>

        {/* Run Type + Project row */}
        <div className="ctr-row">
          <div className="ctr-field">
            <label className="ctr-label">RUN TYPE</label>
            <div className="ctr-select-wrap">
              <select className="ctr-input ctr-select" name="run_type" value={formData.run_type} onChange={handleChange}>
                <option value="SCENARIO">Test Scenarios</option>
                <option value="TEST_CASE">Test Cases</option>
              </select>
            </div>
          </div>

          <div className="ctr-field">
            <label className="ctr-label">PROJECT (FOLDER)</label>
            <div className="ctr-select-wrap ctr-select-icon">
              <select
                className="ctr-input ctr-select"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                required
              >
                <option value="">Select a project...</option>
                {projectFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <svg className="ctr-field-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Subfolder + Build Version row */}
        <div className="ctr-row">
          <div className="ctr-field">
            <label className="ctr-label">SUBFOLDER</label>
            <div className="ctr-select-wrap">
              <select
                className="ctr-input ctr-select"
                name="folder_id"
                value={formData.folder_id}
                onChange={handleChange}
                required
                disabled={!formData.project_id}
              >
                <option value="">
                  {formData.project_id ? 'Select a subfolder...' : 'First select a project'}
                </option>
                {subfolders.map(f => <option key={f.id} value={f.id}>{getFolderPath(f)}</option>)}
              </select>
            </div>
          </div>

          <div className="ctr-field">
            <label className="ctr-label">BUILD VERSION</label>
            <div className="ctr-input-icon-wrap">
              <svg className="ctr-input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
              </svg>
              <input
                className="ctr-input ctr-input-with-icon"
                type="text"
                name="build_version"
                value={formData.build_version}
                onChange={handleChange}
                placeholder="e.g. 2023.10.14-stable"
              />
            </div>
          </div>
        </div>

        {/* Environment */}
        <div className="ctr-field">
          <label className="ctr-label">ENVIRONMENT <span style={{ color: '#dc2626' }}>*</span></label>
          <div className="ctr-select-wrap ctr-select-env">
            <svg className="ctr-env-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.5 19H9a7 7 0 116.71-9h1.79a4.5 4.5 0 110 9z"/>
            </svg>
            <select
              className="ctr-input ctr-select ctr-select-with-icon"
              name="environment"
              value={formData.environment}
              onChange={handleChange}
            >
              <option value="">Select environment...</option>
              {ENVIRONMENTS.map(env => <option key={env.value} value={env.value}>{env.label}</option>)}
            </select>
          </div>
        </div>

        {/* Cycle Label (mandatory grouping) */}
        <div className="ctr-field">
          <label className="ctr-label">CYCLE LABEL <span className="ctr-required">*</span></label>
          <input
            className="ctr-input"
            type="text"
            name="cycle_label"
            value={formData.cycle_label}
            onChange={handleChange}
            placeholder="e.g. DAS-2146 — groups Stage / Preprod / Production runs together"
            list="cycle-label-suggestions"
          />
          {existingCycleLabels.length > 0 && (
            <datalist id="cycle-label-suggestions">
              {existingCycleLabels.map(label => (
                <option key={label} value={label} />
              ))}
            </datalist>
          )}
          <span className="ctr-field-hint">
            {existingCycleLabels.length > 0
              ? `Auto-filled from existing runs for this folder. You can change it.`
              : 'Groups related runs across environments into a Cycle card'}
          </span>
        </div>

        {/* Info box */}
        <div className="ctr-info-box">
          <div className="ctr-info-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10" opacity="0.15"/><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="ctr-info-text">
            <strong>Execution Estimate</strong>
            <span>
              {selectedSubfolder
                ? <>This test run will execute all test {formData.run_type === 'SCENARIO' ? 'scenarios' : 'cases'} in <strong>{selectedSubfolder.name}</strong>{selectedProject ? <> under <strong>{selectedProject.name}</strong></> : ''}.</>
                : <>Select a project and subfolder to see execution details.</>
              }
            </span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="ctr-actions">
          <button type="button" className="ctr-btn-cancel" onClick={() => navigate('/test-runs')} disabled={saving}>
            Cancel
          </button>
          <div className="ctr-actions-right">
            <span title={!isApproved && selectedFolder ? notApprovedReason : ''} style={{ display: 'inline-block' }}>
              <button
                type="submit"
                className="ctr-btn-create"
                disabled={saving || (!!selectedFolder && !isApproved)}
                style={!isApproved && selectedFolder ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
              >
                {saving ? 'Creating...' : (
                  <>
                    Create Test Run
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </>
                )}
              </button>
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateTestRunPage;
